import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Container, CssBaseline, Typography } from '@mui/material';
import { StringParam, useQueryParams } from 'use-query-params';
import Header from '../components/Header/header';
import { useConnection } from '../connection/Application';
import { CbaLanguage, CbaPaperSummary, CbaSubject, readBody, readList } from '../components/Cba/cbaApi';
import CbaGreenHeader, { CbaFilters } from '../components/Cba/CbaGreenHeader';
import CbaInstructions from '../components/Cba/CbaInstructions';
import PaperList from '../components/Cba/PaperList';

/** Only exam papers are listed here. Mock papers will get their own way in. */
const PAPER_TYPE = 'exam';

/**
 * The way into the CBA exam papers: pick a language, subject and grade in the
 * green bar, press Continue, and that subject's papers are listed here.
 *
 * The choices live in `?category`, `?grade` and `?language` so a paper's back
 * link can return to them.
 */
const CbaSubjects: FC = () => {
  const connection = useConnection();
  const [query, setQuery] = useQueryParams({
    category: StringParam,
    grade: StringParam,
    language: StringParam,
  });
  const category = query.category ?? undefined;
  const gradeId = query.grade ?? '';
  const language = (query.language ?? '') as CbaLanguage | '';
  const filters = useMemo<CbaFilters>(
    () => ({ subjectId: category ?? '', gradeId, language }),
    [category, gradeId, language],
  );
  // The bar's language and grade before Continue, which decide the subjects it offers.
  const [draft, setDraft] = useState<{ gradeId: string; language: string }>({ gradeId, language });

  // Back and forward change the applied choices, and the bar follows them.
  useEffect(() => setDraft({ gradeId, language }), [gradeId, language]);

  const [grades, setGrades] = useState<{ id: string; value: string }[]>([]);
  const [subjects, setSubjects] = useState<CbaSubject[]>([]);
  const [subjectsLoaded, setSubjectsLoaded] = useState(false);
  const [papers, setPapers] = useState<CbaPaperSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  // Changing subject or grade quickly can leave an older request answering last.
  const latestRequest = useRef(0);

  useEffect(() => {
    const fetchGrades = async () => {
      try {
        const response = await connection.get('grade/data/all');
        const list = readBody<{ id: string; value: string }[]>(response) ?? [];
        // A grade with no name cannot be picked sensibly, and opening on one
        // would greet the student with an empty page.
        setGrades(list.filter((grade) => (grade.value ?? '').trim() !== ''));
      } catch (e) {
        setGrades([]);
      }
    };
    fetchGrades();
  }, [connection]);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await connection.get('cba/subjects', {
          paperType: PAPER_TYPE,
          ...(draft.gradeId ? { gradeId: draft.gradeId } : {}),
          ...(draft.language ? { language: draft.language } : {}),
        });
        setSubjects(readBody<CbaSubject[]>(response) ?? []);
      } catch (e) {
        setSubjects([]);
      } finally {
        setSubjectsLoaded(true);
      }
    };
    fetchSubjects();
  }, [connection, draft.gradeId, draft.language]);

  // With no subject picked yet, open on the first one that has papers. Replacing
  // the history entry keeps the back button from bouncing straight back here.
  useEffect(() => {
    if (!category && subjects.length > 0) {
      setQuery({ category: subjects[0].id }, 'replaceIn');
    }
  }, [category, subjects, setQuery]);

  const applyFilters = (next: CbaFilters) => {
    setQuery({
      category: next.subjectId || undefined,
      grade: next.gradeId || undefined,
      language: next.language || undefined,
    });
  };

  const fetchPapers = useCallback(async () => {
    if (!category) {
      return;
    }
    const request = ++latestRequest.current;
    setLoading(true);
    setError('');
    try {
      const response = await connection.get('cba/papers', {
        categoryId: category,
        paperType: PAPER_TYPE,
        ...(gradeId ? { gradeId } : {}),
        ...(language ? { language } : {}),
        size: 50,
      });
      if (request === latestRequest.current) {
        setPapers(readList<CbaPaperSummary>(response).data);
      }
    } catch (e) {
      if (request === latestRequest.current) {
        setPapers([]);
        setError('The exam papers could not be loaded. Please try again.');
      }
    } finally {
      if (request === latestRequest.current) {
        setLoading(false);
      }
    }
  }, [connection, category, gradeId, language]);

  useEffect(() => {
    fetchPapers();
  }, [fetchPapers]);

  const subjectName =
    subjects.find((subject) => subject.id === category)?.title ?? papers[0]?.subject?.value ?? '';

  return (
    <>
      <CssBaseline />
      <Header />
      <CbaGreenHeader
        filters={filters}
        subjects={subjects}
        grades={grades}
        onDraftChange={(next) => setDraft({ gradeId: next.gradeId, language: next.language })}
        onContinue={applyFilters}
      />

      <Container sx={{ minHeight: 'calc(100vh - 185px)' }}>
        <Box sx={{ pt: 4, pb: 4 }}>
          <CbaInstructions />

          {error !== '' && (
            <Alert severity='error' sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}

          {subjectName !== '' && (
            <Typography variant='h5' sx={{ fontWeight: 600, mt: 4 }}>
              {subjectName}
            </Typography>
          )}

          {!category ? (
            subjectsLoaded && subjects.length === 0 ? (
              <Alert severity='info' sx={{ mt: 3 }}>
                There are no exam papers for this grade yet. Check back soon.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            )
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error !== '' ? null : papers.length === 0 ? (
            <Alert severity='info' sx={{ mt: 3 }}>
              There are no exam papers in this subject yet. Check back soon.
            </Alert>
          ) : (
            <PaperList papers={papers} compact />
          )}
        </Box>
      </Container>
    </>
  );
};

export default CbaSubjects;
